import { useSEO, breadcrumb } from "../lib/seo";
import { SITE, ASSETS } from "../lib/site";
import PageHero from "../components/PageHero";
import { ShieldCheck, KeyRound, Lock, Eye, Smartphone, AlertTriangle, CheckCircle2 } from "lucide-react";

const PILLARS = [
  { icon: KeyRound, title: "Kata Sandi yang Kuat", desc: "Gunakan kombinasi huruf besar, kecil, angka, dan simbol. Hindari menggunakan tanggal lahir atau nomor identitas." },
  { icon: Lock, title: "Akses Melalui Link Resmi", desc: "Selalu masuk melalui tautan resmi atau halaman link alternatif yang kami sediakan." },
  { icon: Eye, title: "Waspadai Pesan Tidak Dikenal", desc: "Jangan klik tautan dari pesan singkat, email, atau media sosial yang mencurigakan." },
  { icon: Smartphone, title: "Perangkat & Jaringan Aman", desc: "Hindari WiFi publik tanpa pengamanan tambahan, dan pastikan perangkat Anda terlindungi." },
];

const RULES = [
  "Pentingnya menggunakan link resmi.",
  "Hindari situs tiruan yang meniru tampilan resmi.",
  "Periksa alamat website sebelum login.",
  "Jangan membagikan data akun kepada pihak lain.",
  "Gunakan informasi dari halaman resmi.",
  "Aktifkan kebiasaan logout setelah selesai.",
  "Pelajari modus penipuan terbaru secara berkala.",
  "Konfirmasi pesan mencurigakan ke kanal resmi.",
];

export default function Security() {
  useSEO({
    title: "Keamanan Akun Sloto69 | Edukasi Keamanan Digital",
    description: "Pelajari praktik terbaik menjaga keamanan akun Sloto69, mulai dari kata sandi, akses link resmi, hingga kewaspadaan terhadap situs tiruan.",
    keywords: "keamanan akun sloto69, edukasi keamanan sloto69, tips keamanan sloto69",
    canonical: `${SITE.baseUrl}/keamanan`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Keamanan", url: `${SITE.baseUrl}/keamanan` },
    ]),
  });

  return (
    <>
      <PageHero
        eyebrow="Keamanan & Kenyamanan"
        title="Keamanan Akun Sloto69 dan Edukasi Pengguna Indonesia"
        description="Sloto69 menjunjung tinggi prinsip keamanan digital. Temukan panduan praktis untuk menjaga kenyamanan dan ketenangan saat mengakses informasi resmi kami."
        breadcrumb={[{ label: "Keamanan" }]}
      />

      <section className="section-pad" data-testid="security-pillars">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 relative overflow-hidden rounded-3xl">
            <div className="absolute -inset-6 bg-[#10B981]/12 blur-3xl rounded-full pointer-events-none" />
            <img
              src={ASSETS.security}
              alt="Ilustrasi pelindung keamanan digital"
              loading="lazy"
              className="relative card-premium p-2 rounded-3xl w-full h-auto"
            />
          </div>
          <div className="lg:col-span-7">
            <span className="gold-pill">Empat Pilar Keamanan</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Pilar Utama Menjaga Akun Anda</h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => (
                <article key={p.title} data-testid={`security-pillar-${i}`} className="card-premium p-5">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#10B981]/15 border border-[#10B981]/30">
                    <p.icon className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#070710] border-y border-white/5" data-testid="security-rules">
        <div className="shell grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="gold-pill">Aturan Praktis</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight">8 Aturan Keamanan yang Patut Anda Terapkan</h2>
            <p className="mt-4 text-slate-300">
              Kebiasaan kecil namun konsisten dapat memberi dampak besar pada keamanan akun
              digital Anda.
            </p>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {RULES.map((r, i) => (
              <li key={r} data-testid={`security-rule-${i}`} className="card-premium p-4 flex items-start gap-3 text-sm text-slate-300">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10B981] flex-shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad" data-testid="security-warning">
        <div className="shell">
          <div className="card-premium p-7 lg:p-10 flex flex-col lg:flex-row gap-6 items-start">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-semibold">Catatan Khusus untuk Pengguna</h3>
              <p className="mt-3 text-slate-300 leading-relaxed">
                Sloto69 tidak akan pernah meminta data sensitif melalui pesan tidak resmi.
                Apabila Anda menerima permintaan mencurigakan yang mengatasnamakan Sloto69,
                segera abaikan dan verifikasi melalui kanal resmi kami. Edukasi pengguna lain
                juga merupakan bagian penting dalam menjaga ekosistem digital tetap sehat.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
